import {Modal, ModalContent, ModalHeader} from "@/Components/Modal";
import React, {Suspense, useEffect, useState} from "react";
import CampaignIcon from '@mui/icons-material/Campaign';
import {Box, FormControl, MenuItem, Select, Skeleton} from "@mui/material";
import {graphql, PreloadedQuery, usePreloadedQuery, useQueryLoader} from "react-relay";
import type {WhatsNewModalQuery as WhatsNewModalQueryType} from './__generated__/WhatsNewModalQuery.graphql';
import {TypeTag} from "@/Components/WahtsNewModal/TypeTag";
import moment from "moment";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

export interface WhatsNewModalProps {
    onClose: () => void;
}

const whatsNewModalQuery = graphql`
    query WhatsNewModalQuery {
        system {
            releases {
                edges {
                    node {
                        releaseDate
                        version
                        releaseNotes {
                            note
                            task
                            type
                        }
                    }
                }
            }
        }
    }
`;

export function WhatsNewModal({onClose}: WhatsNewModalProps) {

    const [queryRef, loadQuery] = useQueryLoader<WhatsNewModalQueryType>(whatsNewModalQuery);


    useEffect(() => {
        loadQuery({}, {fetchPolicy: "store-or-network"});
    }, [loadQuery]);

    return (
        <Modal onClose={onClose} open={true}>
            {queryRef ? (
                <Suspense fallback={<WhatsNewModalBodySkeleton onClose={onClose}/>}>
                    <WhatsNewModalBody queryRef={queryRef} onClose={onClose}/>
                </Suspense>
            ) : (
                <WhatsNewModalBodySkeleton onClose={onClose}/>
            )}
        </Modal>
    );
}

interface BookingModalBodyProps {
    queryRef: PreloadedQuery<WhatsNewModalQueryType>;
    onClose: () => void;
}

function WhatsNewModalBody({queryRef, onClose}: BookingModalBodyProps) {
    const {t} = useTranslation();
    const data = usePreloadedQuery(whatsNewModalQuery, queryRef);
    const installedVersion = import.meta.env.VITE_VERSION;

    const [selectedVersion, setSelectedVersion] = useState(installedVersion);

    return (
        <>
            <ModalHeader title={t("whatsnew.headline", "Was ist neu?")} icon={<CampaignIcon/>} onClose={onClose}/>
            <Box sx={{px: 2.75, paddingBottom: 2.25}}>
                <FormControl margin="dense">
                    <span>{t("whatsnew.versionDropdownLabel", "Version anzeigen")}</span>
                    <Select variant={"outlined"} size={"small"} value={selectedVersion}
                            onChange={(e) => setSelectedVersion(e.target.value)}>
                        {data.system?.releases?.edges?.map((edge) => (
                            <MenuItem value={edge?.node.version} key={edge?.node.version}>
                                {t("whatsnew.version", "Version")} {edge?.node.version}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <ModalContent>
                {(() => {
                    const release = data?.system?.releases?.edges
                        ?.find((edge) => edge?.node?.version === selectedVersion)?.node;
                    if (!release) return null;
                    return (
                        <>
                            <Box display={"flex"} alignItems={"baseline"} gap={1} marginBottom={"16px"}>
                                <span style={{
                                    fontSize: 15,
                                    fontWeight: 500,
                                    color: "#000"
                                }}>{t("whatsnew.version", "Version")} {release.version}</span>
                                <span style={{
                                    fontSize: 12,
                                    color: "rgba(0,0,0,.54)"
                                }}>{moment(release.releaseDate).format('DD.MM.YYYY')}</span>
                                {selectedVersion === installedVersion && <span style={{
                                    marginLeft: "auto",
                                    fontSize: 11,
                                    color: "#E53935",
                                    fontWeight: 500,
                                    letterSpacing: ".02em"
                                }}>{t("whatsnew.actualVersion", "AKTUELL")}</span>}
                            </Box>
                            <Box display={"flex"} flexDirection={"column"} gap={5}>
                                {release.releaseNotes?.length > 0 ? (
                                    <>
                                        {release?.releaseNotes?.map((releaseNote) => (
                                            <Box display={"grid"} gridTemplateColumns={"92px 1fr"}
                                                 alignItems={"flex-start"}
                                                 gap={10}
                                                 key={releaseNote?.task}>
                                                <TypeTag type={releaseNote?.type || ""}/>
                                                <span style={{
                                                    fontSize: 14,
                                                    lineHeight: "20px",
                                                    color: "#000",
                                                    paddingTop: 1
                                                }}>{releaseNote?.task}: {releaseNote?.note}</span>
                                            </Box>
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        <Box sx={{padding: "40px 0", textAlign: "center"}}>
                                            <CampaignIcon fontSize={"large"} sx={{color: "rgba(0,0,0,.2)"}}/>
                                            <p style={{margin: "10px 0 0", fontSize: 13, color: "rgba(0,0,0,.54)"}}>
                                                {t("whatsnew.noReleaseNotes", "Für diese Version liegen keine Release Notes vor.")}
                                            </p>
                                        </Box>
                                    </>
                                )}

                            </Box>
                        </>
                    );
                })()}
            </ModalContent>
        </>
    )
}

interface BookingModalBodySkeletonProps {
    onClose: () => void;
}

function WhatsNewModalBodySkeleton({onClose}: BookingModalBodySkeletonProps) {

    return (
        <>
            <ModalHeader title={<Skeleton variant="text" width={120}/>} icon={<CampaignIcon/>} onClose={onClose}/>
            <Box sx={{px: 2.75, py: 2.25}}>
                <FormControl margin="dense">
                    <Skeleton variant="text" width={90}/>
                    <Skeleton variant="rectangular" width={200}/>
                </FormControl>
            </Box>
            <ModalContent>
                <Box>
                    <>
                        <Box display={"flex"} alignItems={"baseline"} gap={1} marginBottom={"16px"}>
                            <Skeleton variant="text" width={200}/>
                        </Box>
                        <Box display={"flex"} flexDirection={"column"} gap={5}>
                            <Box display={"grid"} gridTemplateColumns={"92px 1fr"}
                                 alignItems={"flex-start"}
                                 gap={10}
                            >
                                <Skeleton variant="text" width={90}/>
                                <Skeleton variant="text" width={200}/>
                            </Box>
                        </Box>
                    </>
                </Box>

            </ModalContent>
        </>
    )
}
